/* global BX24 */
import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

import '../theme/redesign/tokens.css';
import '../theme/redesign/modal.css';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const TOPIC_LABELS = {
  'Incorrect Product Price': 'Неправильная цена товара',
  'Incorrect Specifications': 'Неправильные характеристики',
  'Incorrect Manufacturing Data': 'Неправильные данные о производстве',
  'Duplicates': 'Дубликаты',
  'Other': 'Другое',
};

const ComplaintsModal = ({ onClose: externalOnClose, userName: propUserName = '', userData = null, products = [] }) => {
  const [show, setShow] = useState(true);
  const [userId, setUserId] = useState(-1);
  const [userName, setUserName] = useState(propUserName || 'Пользователь');
  const [dealId, setDealId] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [complaintText, setComplaintText] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  const dialogRef = useRef(null);

  // Подтягиваем юзера из сделки Bitrix — без правок логики.
  useEffect(() => {
    const setUserFromDeal = async () => {
      if (typeof BX24 === 'undefined' || BX24 === null) {
        return;
      }
      try {
        BX24.init(async () => {
          const placementInfo = BX24.placement?.info?.();
          if (placementInfo?.options?.ID) {
            const dealIdLocal = placementInfo.options.ID.toString();

            const response = await fetch(`${PATH_TO_BACKEND}/api/deal-assigned-user`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ dealId: dealIdLocal }),
            });

            if (response.ok) {
              const data = await response.json();
              if (data.assignedUserId) {
                setUserId(data.assignedUserId);
                setUserName(data.assignedUserName || 'Пользователь');
                setDealId(dealIdLocal);
              }
            }
          }
        });
      } catch (error) {
        console.error('Ошибка при получении ID пользователя:', error);
      }
    };

    setUserFromDeal();
    setShow(true);
    setLoading(false);
  }, [propUserName]);

  const handleClose = useCallback(() => {
    setShow(false);
    if (externalOnClose) {
      externalOnClose();
    }
  }, [externalOnClose]);

  // ESC закрывает модалку.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleSubmit = async () => {
    if (!selectedTopic) {
      alert('Пожалуйста, выберите тему');
      return;
    }

    if ((selectedTopic === 'Incorrect Manufacturing Data' || selectedTopic === 'Other') && !complaintText.trim()) {
      const fieldName = selectedTopic === 'Incorrect Manufacturing Data'
        ? 'комментарий с указанием производителя'
        : 'описание проблемы';
      alert(`Пожалуйста, заполните ${fieldName}`);
      return;
    }

    setLoading(true);
    try {
      let productNames = null;
      if ((selectedTopic === 'Incorrect Product Price' || selectedTopic === 'Incorrect Specifications') && products && products.length > 0) {
        const validProducts = products.filter((product) => product && product.name && product.name.trim() !== '');
        if (validProducts.length > 0) {
          productNames = validProducts.map((product) => product.name).join(', ');
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/complaints-and-suggestions-proxy-to-google-sheets`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            complaintText: complaintText,
            userName: userName,
            userId: userId,
            dealId: 'https://td.monolit-crm.ru/crm/deal/details/' + dealId,
            topic: TOPIC_LABELS[selectedTopic] || selectedTopic,
            productData: (selectedTopic === 'Incorrect Product Price' || selectedTopic === 'Incorrect Specifications') ? productNames : null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Ошибка при отправке жалобы');
      }

      const result = await response.json();

      if (result.success) {
        setComplaintText('');
        setSelectedTopic('');
        handleClose();
      } else {
        throw new Error(result.message || 'Ошибка при отправке жалобы');
      }
    } catch (error) {
      console.error('Ошибка при отправке жалобы:', error);
      alert('Произошла ошибка при отправке. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  // Рендерим в body чтобы не попасть в trap backdrop-filter родителей
  const portalTarget = document.body;

  const placeholderText = selectedTopic === 'Incorrect Manufacturing Data'
    ? 'Укажите производство'
    : 'Подробнее опишите вашу проблему';

  const isSubmitDisabled =
    loading
    || !selectedTopic
    || ((selectedTopic === 'Incorrect Manufacturing Data' || selectedTopic === 'Other') && !complaintText.trim());

  return ReactDOM.createPortal(
    <div
      className="rd-modal-overlay"
      onClick={handleClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="rd-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rd-complaints-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="rd-modal__close"
          onClick={handleClose}
          aria-label="Закрыть"
          title="Закрыть"
        />

        <div className="rd-modal__head">
          <h3 id="rd-complaints-title" className="rd-modal__title">
            Жалобы и&nbsp;предложения
          </h3>
          <p className="rd-modal__subtitle">
            Уважаемый пользователь, если у&nbsp;вас есть жалобы или предложения
            по&nbsp;использованию нашего сервиса, пожалуйста, заполните форму ниже.
          </p>
        </div>

        <div className="rd-modal__body">
          <div className="rd-field">
            <label htmlFor="rd-complaints-topic" className="rd-field__label">Тема</label>
            <select
              id="rd-complaints-topic"
              className="rd-modal__select"
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              disabled={loading}
            >
              <option value="" disabled>Выберите тему</option>
              {Object.entries(TOPIC_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="rd-field">
            <label htmlFor="rd-complaints-text" className="rd-field__label">Сообщение</label>
            <textarea
              id="rd-complaints-text"
              className="rd-modal__textarea"
              value={complaintText}
              onChange={(e) => setComplaintText(e.target.value)}
              placeholder={placeholderText}
              maxLength={2000}
              rows={8}
              disabled={loading}
            />
            <div className="rd-modal__counter">
              {complaintText.length} / 2000
            </div>
          </div>
        </div>

        <div className="rd-modal__foot">
          <button
            type="button"
            className="rd-modal__submit"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {loading ? 'Отправка…' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default ComplaintsModal;
